// import { Platform, NativeModules } from 'react-native';
import logger from '../utils/Logger';
import { default as trDE } from '../../locale/de.json';
import { default as trUS } from '../../locale/en_US.json';
import Gettext from 'node-gettext'
import { sprintf } from 'sprintf-js';
import moment from 'moment-timezone';
// Workaround for "Error: Requiring unknown module "./locale/de"
// https://github.com/moment/moment/issues/3624#issuecomment-406451318
// https://github.com/moment/moment/issues/5789
// https://github.com/jalaali/moment-jalaali/issues/142
// https://github.com/facebook/react-native/issues/1629
import 'moment/locale/de';
import { noValueSign, ValueAndUnit } from '../types';

let extensions:any[] = [];

/** 1 US liquid gallon expressed in liters */
const usLiquidGallonInLiter = 3.785411784;
/** 1 cubic foot expressed in US liquid gallon */
const cubicFootInUsLiquidGallon = 7.4805194805;
/** 1 Mile expressed in Kilometer */
const mileInKilometer = 1.609344;
/** 1 Foot expressed in meter */
const footInMeter = 0.3048;
/** Fahrenheit scale slope compared to the Celsius scale */
const fahrenheitSlope = 9 / 5;
/** Fahrenheit scale offset compared to the Celsius scale */
const fahrenheitOffset = 32;
/** Fluid ounces expressed in milliliters */
const fluidOunceInMilliLiter = 29.5735296875;
/** 1 PSI equals 6.894757 kPa */
const KPA_PER_PSI = 6.894757;
const PSI_PER_KPA = 1 / KPA_PER_PSI; // 0.1450377377...
/** 1 bar equals 14.5037738 PSI */
const PSI_PER_BAR = 14.5037738;
const BAR_PER_PSI = 1 / PSI_PER_BAR;

export enum measurementSystems {
    IMPERIAL = "imperial",
    METRIC = "metric",
}

type Rounding = "floor" | "round" | "ceil";

export class Locale {
    _type = 'Locale';
    monitorErrors = false;
    supportedLocales = [];
    gtInstance = new Gettext();
    /** E.g: 'en_US'. setLocale() initializes it. */
    currentLocale = null;
    /** Make time formatters to use this time zone when printing a timestamp. Leave empty to use OR default zone. */
    timezone = '';
    /** Full date with year, month and day in Moment.js format E.g: MM/DD/YYYY */
    dateFormat = 'MM/DD/YYYY';
    /** Short date, three letter month and day in Moment.js format E.g: MMM D  */
    shortDateFormat = 'MMM D';
    /** 12 hour or 24 hour clock convention. */
    timeHours = 12;
    /** E.g: 'sunday' */
    firstDayOfWeek = 'sunday';
    /** E.g: 'f', 'c' */
    temperatureType = 'f';
    /** E.g: '$' */
    currencySymbol = '$';
    /** E.g: metric, imperial */
    volumeUnit = measurementSystems.IMPERIAL;
    /** E.g: metric, imperial */
    lengthUnit = measurementSystems.IMPERIAL;
    decimalSeparator = ".";
    digitGroupingSeparator = ",";  

    daysOfWeek = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
    ];

    constructor(locale = 'en_US') {
        this.gtInstance.on('error', e => {
            // For monitoring untranslated expressions
            if (this.monitorErrors) {
                logger.warn('[Locale] Gettext: ' + e);
            }
        });
        this.loadMessages();

        //let locale = 'en_US';
        /*let locale =
            Platform.OS === 'ios'
                ? NativeModules.SettingsManager.settings.AppleLocale ||
                NativeModules.SettingsManager.settings.AppleLanguages[0] //iOS 13
                : NativeModules.I18nManager.localeIdentifier;*/
        //logger.debug('[Locale] DEVICE LANGUAGE', locale);
        
        if (!this.supportedLocales.includes(locale)) {
            // Try to found a suitable locale where at least the language mathces
            const tmp = this.supportedLocales.find(item => 
                item.substr(0, 2) === locale?.substr(0, 2)
            );
            if (tmp) {
                //logger.debug('[Locale] DEVICE LANGUAGE is not supported, using closest:', tmp);
            } else {
                //logger.debug('[Locale] DEVICE LANGUAGE is not supported, using en_US');
            }
            locale = tmp ?? 'en_US';
        } 
        this.setLocale(locale);

        this._type = 'Locale';
    }

    /** @deprecated The class is now exported and can be instantiated. */
    getNewInstance(locale = 'en_US') {
        return new Locale(locale);
    }

    loadMessages() {
        this.gtInstance.addTranslations('de', 'messages', this.loadExtensions(trDE));
        if (!this.supportedLocales.includes(trDE.headers.Language)) {
            this.supportedLocales.push(trDE.headers.Language);
        }

        this.gtInstance.addTranslations('en_US', 'messages', this.loadExtensions(trUS));
        if (!this.supportedLocales.includes(trUS.headers.Language)) {
            this.supportedLocales.push(trUS.headers.Language);
        }

        logger.debug('[Locale] loaded locales:', this.supportedLocales);
    }
    
    loadExtensions(tr) {
        let trans = JSON.parse(JSON.stringify(tr));
        let exArr = extensions.filter(ext => ext.headers.Language === trans.headers.Language);
        logger.debug("[Locale] Loading " + exArr.length + " extensions for " + trans.headers.Language);

        for (let ext of exArr) {
            Object.assign(trans.translations[""], ext.translations[""]);
            logger.debug("[Locale] Loaded ext for " + ext.headers.Language);
        }

        return trans;
    }

    addExtension(translation) {        
        const translations = Array.isArray(translation) ? translation : [ translation ];
        translations.forEach(item => {
            logger.debug("[Locale] Adding extension for " + item.headers.Language);
            extensions.push(item)
        });
        this.loadMessages();
    }

    get type() {
        return this._type;
    }

    set type(value) {
        this._type = value;
    }
    
    getLocale() {
        return this.currentLocale;
    }

    setTimeZone(timeZone: string) {
        this.timezone = timeZone;
    }

    setLocale(locale) {
        try {
            logger.debug('[Locale] setLocale():', locale);
            if (!this.supportedLocales.includes(locale)) {
                logger.error('[Locale] the requested locale is not supported:', locale, 'Skip setting locale. Supported locales:', this.supportedLocales);
                return false;
            }
            this.currentLocale = locale;
            this.gtInstance.setLocale(locale);

            moment.locale(locale);
        } catch(e) {
            logger.error('[Locale] setLocale():', e);
        }
    }
    /** @return {object} */
    getLocaleInfo() {
        return ({
            dateFormat: this.dateFormat,
            shortDateFormat: this.shortDateFormat,
            firstDayOfWeek: this.firstDayOfWeek,
            locale: this.currentLocale,
            timeHours: this.timeHours,
            temperatureType: this.temperatureType,
            currencySymbol: this.currencySymbol,
            lengthUnit: this.lengthUnit,
            volumeUnit: this.volumeUnit,
            decimalSeparator: this.decimalSeparator,
            digitGroupingSeparator: this.digitGroupingSeparator,
            timezone: this.timezone
        });
    }
    /**
     * @param {object} localeInfo
     * @param {string} localeInfo.dateFormat In Moment.js format, e.g: 'MM/DD/YYYY'
     * @param {string} localeInfo.shortDateFormat In Moment.js format, e.g: 'MMM D'
     * @param {string} localeInfo.firstDayOfWeek E.g: 'monday'
     * @param {string} localeInfo.locale E.g: 'en_US'
     * @param {number} localeInfo.timeHours E.g: 12
     * @param {string} localeInfo.temperatureType E.g: 'c', 'f'
     * @param {string} localeInfo.currencySymbol E.g: '$', '€'
     * @param {string} localeInfo.distanceType E.g: 'km', 'mi' DEPRICATED. Use lengthUnit instead.
     * @param {string} localeInfo.lengthUnit E.g: 'metric', 'imperial'
     * @param {string} localeInfo.volumeUnit E.g: 'metric', 'imperial'
     * @param {string} localeInfo.decimalSeparator E.g: '.', ','
     * @param {string} localeInfo.digitGroupingSeparator E.g: ',', ' ', ''
     * @param {string} localeInfo.timezone E.g: 'America/New_York'
     */
    setLocaleInfo(localeInfo) {
        logger.debug('[Locale] setLocaleInfo():', localeInfo);
        const langCode = (localeInfo.locale?.substr && localeInfo.locale.substr(0,  2));
        if (localeInfo.firstDayOfWeek) this.firstDayOfWeek = localeInfo.firstDayOfWeek;
        if (localeInfo.timeHours) this.timeHours = localeInfo.timeHours;
        if (localeInfo.dateFormat) {
            this.dateFormat = localeInfo.dateFormat;
        } else {
            if (langCode === "de") this.dateFormat = "YYYY.MM.DD";
        }
        if (localeInfo.shortDateFormat) {
            this.shortDateFormat = localeInfo.shortDateFormat;
        } else {
            if (langCode === "de") this.shortDateFormat = 'D. MMM';
        }
        if (localeInfo.temperatureType) this.temperatureType = localeInfo.temperatureType.toLowerCase();
        if (localeInfo.distanceType) {
            switch (localeInfo.distanceType.toLowerCase()) {
                case 'mi': this.lengthUnit = measurementSystems.IMPERIAL; break;
                case 'km': this.lengthUnit = measurementSystems.METRIC; break;
            }
        }
        if (localeInfo.currencySymbol) this.currencySymbol = localeInfo.currencySymbol;
        if (localeInfo.lengthUnit) this.lengthUnit = localeInfo.lengthUnit;
        if (localeInfo.volumeUnit) this.volumeUnit = localeInfo.volumeUnit;
        if (localeInfo.decimalSeparator) this.decimalSeparator = localeInfo.decimalSeparator;
        if (localeInfo.digitGroupingSeparator) this.digitGroupingSeparator = localeInfo.digitGroupingSeparator;
        if (localeInfo.timezone) this.timezone = localeInfo.timezone;

        this.setLocale(localeInfo.locale ?? this.currentLocale);
    }

    /**
     * @param {string} msgid String to be translated when count is not plural
     * @param {...*} params Parameters to pass in to sprintf formatter. 
     */
    gettext(...spArgs) {
        try {
            spArgs[0] = this.gtInstance.gettext.apply(this.gtInstance, [spArgs[0]]);
            return sprintf.apply(sprintf, spArgs);
        } catch (e) {
            //logger.error('[Locale] gettext():', e, 'args:', arguments);
            return (arguments[0]);
        }
    }

    getTimeFormat(timeHours, withSeconds = false, withLeadingZeros = true) {
        timeHours = timeHours ?
            Number(timeHours.toString().substr(0, 2)) : // in case "24hour" comes in
            this.timeHours;

        const timeFormat =
            ( timeHours === 12 ?
                (withLeadingZeros ? 'hh' : 'h') :
                (withLeadingZeros ? 'HH' : 'H')
            )
            + ':mm'
            + (withSeconds ? ':ss' : '')
            + (timeHours === 12 ? 'a': '');

        return timeFormat;
    }

    /**
     * @param {string} msgid String to be translated when count is not plural
     * @param {string} msgidPlural String to be translated when count is plural
     * @param {number} count Number count for the plural
     * @param {...*} params Parameters to pass in to sprintf formatter. 
     */
    ngettext(...args) {
        try {
            let spArgs = [ 
                this.gtInstance.ngettext.apply(this.gtInstance, [ arguments[0], arguments[1], arguments[2] ]),
                ...[...arguments].slice(2)
            ];
            return sprintf.apply(sprintf, spArgs);
        } catch (e) {
            //console.error('[Locale] gettext():', e, 'args:', arguments);
            return (arguments[0]);
        }
    }

    pgettext(...args) {
        return this.gtInstance.pgettext.apply(this.gtInstance, args);
    }

    formatCost(value: number, decimals?: number | false, rounding?: Rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.currencySymbol === '$') {
            return this.currencySymbol + this.formatNumber(
                this.round(value, decimals, rounding)
            );
        } else {
            return this.formatNumber(
                this.round(value, decimals, rounding)
            ) + this.currencySymbol;
        }
    }

    toDate(datetime:number|string|Date):Date|null {
        if (!(datetime instanceof Date) && typeof datetime !== 'number' && typeof datetime !== 'string') {
            return null;
        }
        if (typeof datetime === 'number' || typeof datetime === 'string') {
            datetime = new Date(datetime);
        }
        if (isNaN(datetime.getTime())) {
            // Invalid date
            return null;
        }
        return datetime;
    }

    isConvertible(value: any): boolean {
        return !(isNaN(value) || value === null || typeof value === 'boolean' || value === "");
    }

    /**
     * @param value 
     * @param unit 
     * @param mode If defined as string then returns value and unit as a concatenated string using string defined by `mode` as separator.
     * Returns value and object as an object otherwise.
     * @returns 
     */
    returnValueAndUnit(value: string, unit?: string, asObject = false, separator = ""): ValueAndUnit | string {
        if (asObject) {
            if (typeof value === "undefined") return { value: noValueSign };
            return { value, separator, unit };
        }
        if (typeof value === "undefined") return noValueSign;
        return value + separator + unit;
    }


    /**
     * Rounds input using the given precision and by the given method.
     * Skip rounding for for values that can not be processed as number. 
     * @see isConvertible()
     */
    round(value: number, decimals: number | false = 0, rounding: Rounding = "round"): number | string {
        if (decimals === false) return value;
        if (!this.isConvertible(value)) return value;
        const factor = Math.pow(10, decimals);
        const factored = value * factor;
        value = rounding === "round" ?
            Math.sign(factored) * Math[ rounding ](Math.abs(factored)) / factor :
            Math[ rounding ](factored) / factor
    
        return value;
    }

    /**
     * Rounds value using the given precision and by the given method.
     * Skip rounding in case the number seems incomplete e.g:
     * when user is typing in e.g. "0.101" but the user is only at the 4th characterO "0.10"
     * @param value Value to round
     * @param precision Number of decimals.
     * 0 will round to integer
     * false will not round at all.
     * @param round Rounding method.
     * round: closest integer, E.g:  1.9 -> 2, -1.9 -> -2
     * floor: next integer at the "left side" E.g: 1.9 -> 1, -1.9 -> -2
     * ceil: next integer at the "right side" E.g: 1.9 -> 2, -1.9 -> -1
     * @returns Return rounded value as Number, otherwise return original value and type
     */
    roundIfReady(value: any, precision: number | false, rounding: Rounding = "round"): number | string {
        // Don't want to round at all.
        // Doing this test and return here, instead of all the places where round() was called.
        if (precision === false) return value;

        let trimmmed = value.toString().trim();
        if (trimmmed.indexOf(".") === 0) {
            trimmmed = "0" + trimmmed;
        }
        trimmmed = trimmmed.replace("-.", "-0.");
        
        // Don't touch possibly incomplete numbers e.g: 0. or 0.10
        // Trying to detect this by converting it to Number and check if it changes it.
        if (Number(trimmmed).toString() !== trimmmed.toString()) {
            return value;
        }

        const parsed = parseFloat(value);

        if (!Number.isFinite(parsed)) {
            return value;
        }

        const factor = Math.pow(10, precision as number);
        const factored = parsed * factor;
        const rounded = rounding === "round" ?
            Math.sign(factored) * Math[ rounding ](Math.abs(factored)) / factor :
            Math[ rounding ](factored) / factor
        ;

        if (Object.is(rounded, -0)) {
            return 0;
        }

        return rounded;
    }


    formatNumber(value: any, decimals: number | false = false, round: Rounding = 'round'): string {
        if (!this.isConvertible(value)) return value;
        value = this.round(value, decimals, round);
        const [ integerPart, fractionalPart ] = value.toString().split(".");
        let integerPartFormatted = '';
        for (let i = 0; i < integerPart.length; i++) {
            integerPartFormatted += integerPart[i];
            if ((integerPart.length - i -1) % 3 === 0 && i < (integerPart.length - 1)) {
                integerPartFormatted += this.digitGroupingSeparator;
            }
        }
        return integerPartFormatted.trim() + (
            fractionalPart ? this.decimalSeparator + fractionalPart :
            ''
        );
    }

    formatDateTime(datetimeInput:number|string|Date, withSeconds = false, omitDateIfToday = false, timeZone?: string): string {
        const datetime = this.toDate(datetimeInput);
        if (!datetime) {
            return "";
        }

        const now = moment(new Date());
        let momentDate = moment(datetime);
        if (timeZone) {
            momentDate = momentDate.tz(timeZone);
        } else if (this.timezone) {
            momentDate = momentDate.tz(this.timezone);
        }

        let showDate = true;
        if (omitDateIfToday && (
            momentDate.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')
        )) {
            showDate = false;
        }

        let str = '';

        if (showDate) {
            str = this.formatDate(datetime, timeZone) + ' ';
        }
        str += this.formatTime(datetime, withSeconds, undefined, timeZone);
        
        return str;
    }

    formatTime(datetime, withSeconds = false, withLeadingZeros = false, timezone?: string) {
        datetime = this.toDate(datetime);
        if (!datetime) {
            return "";
        }
        let ret = moment(datetime);
        if (timezone) {
            ret = ret.tz(timezone);
        } else if (this.timezone) {
            ret = ret.tz(this.timezone);
        }
        return ret.format(this.getTimeFormat(undefined, withSeconds, withLeadingZeros));
    }

    formatDate(datetimeInput: number | string | Date, timeZone?: string) {
        const datetime = this.toDate(datetimeInput);
        if (!datetime) {
            return "";
        }
        let ret = moment(datetime);
        if (timeZone) {
            ret = ret.tz(timeZone);
        } else if (this.timezone) {
            ret = ret.tz(this.timezone);
        }
        return ret.format(this.dateFormat);
    }

    formatShortDate(timestamp:Date, timezone?: string) {
        let ret = moment(timestamp);
        if (timezone) {
            ret = ret.tz(timezone);
        } else if (this.timezone) {
            ret = ret.tz(this.timezone);
        }
        return ret.format(this.shortDateFormat);
    }

    /**
     * Automatically choose unit for a given time interval.
     * Input e.g:
     * 25, 130, 10000s
     * Output e.g:
     * 25 seconds, 2 minutes, 3 hours.
     * 25 seconds, 2m 10s, 2h 47m (showSecondary=true)
     * 1 minute, 2m 10s, 2h 47m (showSecondary=true, floor=60)
     * 25 seconds, 3 minutes, 3 hours. (round=ceil)
     * 
     * @param {number} seconds
     * @param {boolean} [showSecondary=false]
     * @param {number} [floor=0] smallest value to down into. E.g. 60 makes 25s as 1m
     * @param {string} [round=round] E.g. round, floor, ceil
     */
    formatTimeInterval(seconds: number, showSecondary = false, floor = 0, round: Rounding = 'round') {
        if (isNaN(seconds)) return seconds;
        seconds = Math.max(floor, seconds);
        if (seconds < 60) {
            return this.ngettext("%1$d second", "%1$d seconds", Math[round](seconds));
        } else if (seconds < 3600) { // seconds is less than 60 minutes
            let secondary = Math[round](seconds % 60);
            if (showSecondary && secondary > 0 && secondary < 60 && floor < 60) { // If 60sec is the minimum to show possibly seconds are not needed to be shown
                return this.gettext('%1$dm %2$ds', Math.floor(seconds / 60), secondary);
            } else {
                return this.ngettext('%1$d minute', '%1$d minutes', Math[round](seconds / 60));
            }
        } else if (seconds < 24 * 3600) { // seconds is less than 24h
            let secondary = Math[round]((seconds % 3600) / 60);
            if (showSecondary && secondary > 0 && secondary < 60) {
                return this.gettext('%1$dh %2$dm', Math.floor(seconds / 3600), secondary);
            } else {
                return this.ngettext("%1$d hour", "%1$d hours", Math[round](seconds / 3600));
            }
        } else {
            let secondary = Math[round]((seconds % (24 * 3600)) / 60 / 60);
            if (showSecondary && secondary > 0 && secondary < 24) {
                return this.gettext('%1$dd %2$dh', Math.floor(seconds / 24 / 3600), secondary);
            } else {
                return this.ngettext("%1$d day", "%1$d days", Math[round](seconds / 24 / 3600));
            }
        }
    }
    formatTimeIntervalShort(seconds) {
        if (seconds < 60) {
            return this.gettext("%1$ds", seconds);
        } 
        if (seconds % 60 === 0)
            return this.gettext("%1$dm", Math.floor(seconds / 60));
        return this.gettext("%1$dm %2$ds", Math.floor(seconds / 60), seconds % 60);
    }

    formatTimeRelative(datetime, since = new Date()) {
        let ret = moment(datetime);
        if (this.timezone) {
            ret = ret.tz(this.timezone);
        }
        return ret.from(moment(since));
    }

    /**
     * Today at 5:17pm / 5:17pm today
     * Yesterday at 6:10pm / 6:10pm yesterday
     * on 5/6/2021 at 9:08pm / 9:08pm on 5/6/2021
     * @param datetime Timestamp to pass as argument to MomentJS
     * @param [dateFormat] Date format for MomentJS.
     * @param [timeFormat] Time format for MomentJS.
     * @param [addAt=false] Set true for add the "at" string to the output. E.g. Today at xxx.
     * @param [timeZone] if provided, formatrs time in this time zone.
     * Otherwise falls back to the instance time zone (`this.timezone`, set via {@link setLocale}).
     * @param [reverseOrder=false] When true, returns time before the date label. E.g. "9:26am today".
     */
    formatTimeHumanFriendy(datetime: Date | string | number, dateFormat?: string, timeFormat?: string, addAt = false, timeZone?: string, reverseOrder = false) {
        let dateFormatToUse = dateFormat ?? this.dateFormat;
        let now = moment(new Date());
        const timeZoneToUse = timeZone ?? this.timezone;

        if (timeZoneToUse) now = now.tz(timeZoneToUse);

        let mmt = moment(datetime);
        if (timeZoneToUse) mmt = mmt.tz(timeZoneToUse);

        let tomorrow = moment(new Date(Date.now() + 1000 * 60 * 60 * 24));
        if (timeZoneToUse) tomorrow = tomorrow.tz(timeZoneToUse);

        let yesterday = moment(new Date(Date.now() - 60*60*24*1000));
        if (timeZoneToUse) yesterday = yesterday.tz(timeZoneToUse);

        if (!timeFormat) {
            timeFormat = this.getTimeFormat(undefined, false, false)
        }

        const timeStr = mmt.format(timeFormat);
        // isDate=true uses "on"/"at" prepositions; relative labels (Today/Yesterday/etc.) use lowercase in reverse
        const compose = (label: string, isDate = false) => {
            if (reverseOrder) {
                return isDate
                    ? timeStr + (addAt ? ' on ' : ' ') + label
                    : timeStr + ' ' + label;
            }
            return isDate
                ? (addAt ? 'on ' : '') + label + ' ' + (addAt ? 'at ' : '') + timeStr
                : label + ' ' + (addAt ? 'at ' : '') + timeStr;
        };

        //console.log(datetime, dateFormat, timeHours, timeFormat);
        if (now.format('YYYYMMDD') === mmt.format('YYYYMMDD')) {
            return compose(this.gettext('Today'));
        }
        if (tomorrow.format('YYYYMMDD') === mmt.format('YYYYMMDD')) {
            return compose(this.gettext('Tomorrow'));
        }
        if (yesterday.format('YYYYMMDD') === mmt.format('YYYYMMDD')) {
            return compose(this.gettext('Yesterday'));
        }
        if (Math.abs(Date.now() - mmt.toDate().getTime()) < 6*60*60*24*1000) { // If date is +/- 6 days from now
            return compose(this.gettext(this.daysOfWeek[mmt.day() === 0 ? 6 : mmt.day() - 1]));
        }

        return compose(mmt.format(dateFormatToUse), true);
    }

    formatTimeCalendar(datetime, since = new Date()) {
        let ret = moment(datetime);
        if (this.timezone) {
            ret = ret.tz(this.timezone);
        }
        return ret.calendar(since);
    }
    

    /*
     * FORMATTERS for getting measurement units
     */

    formatTemperatureUnit(addDegreeSymbol = true, addUnit = true) {
        let unit = '' +
            (addDegreeSymbol ? '°' : '') +
            (addUnit ? this.temperatureType.toUpperCase() : '')
        ;
        return unit;
    }

    formatVolumeUnitGallonLiter(short = false, value = 1) {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return (short ?
                this.gettext('Gal') :
                this.ngettext('Gallon', "Gallons", value)
            );
        } else {
            return (short ?
                this.gettext('L') :
                this.ngettext("Liter", "Liters", value)
            );
        }
    }

    formatPressureUnit(short = false, value: number, internalUnit = "psi", localeUnit: "bar" | "pa") {
        if (this.lengthUnit === measurementSystems.IMPERIAL) {
            if (internalUnit === "psi") {
                return (short ?
                    this.gettext('PSI') :
                    this.ngettext('pound per square inch', "pounds per square inch", value)
                );
            } else {
                logger.error("[Locale] invalid argument: internalUnit:", internalUnit);
            }
        } else {
            if (localeUnit === "bar") {
                return (short ?
                    this.gettext('bar') :
                    this.ngettext('bar', "bar", value)
                );
            } else if (localeUnit === "pa") {
                return (short ?
                    this.gettext('kPa') :
                    this.ngettext('kilopascal', "kilopaskals", value)
                );
            } else {
                logger.error("[Locale] invalid argument: localeUnit:", localeUnit);
            }
        }
        
    }

    /**
     * @param [short=false] False: localised unit e.g: Ounce/Ounces/milliliter/milliliters
     * True: localised unit e.g: Oz/mL
     * @param [value=1] Will be passed in to ngettext() for decining to return the normal or the prulal form.
     * Useful if the unit is placed into a sentence e.g:
     * "For every Ounces needed the pump will run for 10 seconds."
     * @returns
     */
    formatVolumeUnitOuncesMilliliter(short:boolean = false, value:number = 1):string {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return (short ?
                this.gettext('Oz') :
                this.ngettext("Ounce", "Ounces", value)
            );
        } else {
            return (short ?
                this.gettext('mL') :
                this.ngettext("milliliter", "milliliters", value)
            );
        }
    }

    formatVolumeUnitOuncesLiter(short: boolean = false, value: number = 1): string {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return (short ?
                this.gettext('Oz') :
                this.ngettext("Ounce", "Ounces", value)
            );
        } else {
            return (short ?
                this.gettext('L') :
                this.ngettext("Liter", "Liters", value)
            );
        }
    }
    formatVolumeUnitOuncesDeciliter(short: boolean = false, value: number = 1): string {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return (short ?
                this.gettext('Oz') :
                this.ngettext("Ounce", "Ounces", value)
            );
        } else {
            return (short ?
                this.gettext('dL') :
                this.ngettext("Liter", "Liters", value)
            );
        }
    }

    formatVolumeCapacityUnitPerDay() {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return this.gettext('GPD');
        } else {
            return this.gettext('L/day');
        }
    }
    formatVolumeCapacityUnitPerHour() {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return this.gettext('GPH');
        } else {
            return this.gettext('L/h');
        }
    }
    formatVolumeCapacityUnitPerMinute() {
        if (this.volumeUnit === measurementSystems.IMPERIAL) {
            return this.gettext('GPM');
        } else {
            return this.gettext('L/min');
        }
    }

    formatTimeUnitM() {
        return this.pgettext('Abbreviation of minute.', 'm');
    }
    formatTimeUnitMin() {
        return this.pgettext('Abbreviation of minute.', 'min');
    }
    formatTimeUnitMinute(value? : number) {
        return this.ngettext('minute', "minutes", value);
    }
    formatTimeUnitH() {
        return this.pgettext('Abbreviation of hour.', 'h');
    }


    /*
     * FORMATTERS for unit conversion functions
     * Does conversion if needed. Applies rounding if requested.
     * Adds unit and number formatting.
     */

    /**
     * @param {Array|string|number} value
     * @param {number} decimals
     * @param {string} [rounding=round] E.g. round, floor, ceil
     * @param {boolean} [addDegreeSymbol=true]
     * @param {boolean} [addUnit=true]
     */
    formatTemperatureFromFahrenheit(value, decimals = 1, rounding: Rounding = 'round', addDegreeSymbol = true, addUnit = true, asObject = false): ValueAndUnit | string {
        const unit = this.formatTemperatureUnit(addDegreeSymbol, addUnit);
        if (Array.isArray(value)) {
            // Array could be passed in from e.g a temperature range selector component

            let tempFrom = value[0];
            let tempTo = value[1];
            if (!this.isConvertible(tempFrom)) {
                tempFrom = null;
            } else {
                tempFrom = this.formatNumber(this.translateFahrenheit2locale(tempFrom, decimals, rounding));
            }
            if (!this.isConvertible(tempTo)) {
                tempTo = null;
            } else {
                tempTo = this.formatNumber(this.translateFahrenheit2locale(tempTo, decimals, rounding));
            }
            let val = "";
            if (tempFrom === null && tempTo === null) {
                val = '';
            } else if (tempFrom === null) {
                val = '< ' + tempTo;
            } else if(tempTo === null) {
                val = '> ' + tempFrom;
            } else {
                val = tempFrom + '-' + tempTo;
            }
            return this.returnValueAndUnit(val, unit, asObject, " ");
        } else {
            const val = this.formatNumber(this.translateFahrenheit2locale(value, decimals, rounding));
            return this.returnValueAndUnit(val, unit, asObject, " ");
        }
    }

    formatTemperatureDiffFromFarenheitDiff(value, decimals?: number, rounding?: Rounding, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateFahrenheitDiff2locale(value, decimals, rounding)),
            this.formatTemperatureUnit(),
            asObject, " "
        );
    }

    formatDistanceFromMile(value, decimals, rounding, asObject = false) {
        let unit: string;
        if (this.lengthUnit === measurementSystems.IMPERIAL) {
            unit = 'mi';
        } else {
            unit = 'km';
        }
        return this.returnValueAndUnit(
            this.formatNumber(this.translateMile2locale(value, decimals, rounding)),
            unit, asObject, " "
        );
    }
    formatDistanceFromKm(value, decimals, rounding, asObject = false) {
        let unit;
        if (this.lengthUnit === measurementSystems.IMPERIAL) {
            unit = 'mi';
        } else {
            unit = 'km';
        }
        return this.returnValueAndUnit(
            this.formatNumber(this.translateKm2locale(value, decimals, rounding)),
            unit, asObject, " "
        );
    }

    formatSpeedFromMph(value, decimals, rounding, asObject = false) {
        let unit;
        if (this.lengthUnit === measurementSystems.IMPERIAL) {
            unit = this.gettext('Mph');
        } else {
            unit = this.gettext('km/h');
        }
        return this.returnValueAndUnit(
            this.formatNumber(this.translateMile2locale(value, decimals, rounding)),
            unit, asObject, " "
        );
    }

    formatVolumeFromGallon(value: number, decimals?: number, rounding?: Rounding, short?: boolean, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateGallon2locale(value, decimals, rounding)),
            this.formatVolumeUnitGallonLiter(short, value),
            asObject, " "
        );
    }

    formatVolumeFromOunces(value, decimals, rounding, short:boolean, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateOunces2locale(value, decimals, rounding)),
            this.formatVolumeUnitOuncesMilliliter(short, value),
            asObject, " "
        );
    }

    formatVolumeCapacityFromGPM(value, decimals?, rounding?, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateGallon2locale(value, decimals, rounding)),
            this.formatVolumeCapacityUnitPerMinute(),
            asObject, " "
        );
    }

    formatVolumeCapacityFromGPH(value, decimals?, rounding?, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateGallon2locale(value, decimals, rounding)),
            this.formatVolumeCapacityUnitPerHour(),
            asObject, " "
        );
    }

    formatVolumeCapacityFromGPD(value, decimals?, rounding?, asObject = false) {
        return this.returnValueAndUnit(
            this.formatNumber(this.translateGallon2locale(value, decimals, rounding)),
            this.formatVolumeCapacityUnitPerDay(),
            asObject, " "
        );
    }

    formatPercentage(value: number, decimals?: number, rounding?: Rounding) {
        const rounded = this.formatNumber(value, decimals, rounding);
        return `${rounded}%`;
    }

    formatPressureFromPsi(value: any, decimals: number | false, rounding: Rounding, short: boolean, asObject = false, localeUnit: "bar" | "pa" = "pa") {
        return this.returnValueAndUnit(
            this.formatNumber(this.translatePressureToLocale(value, decimals, rounding, localeUnit)),
            this.formatPressureUnit(short, value, "psi", localeUnit),
            asObject, " "
        );
    }

    formatBytes(value: any) {
        const k = 1000;
        const m = k * 1000;
        const g = m * 1000;
        const t = g * 1000;
        const p = t * 1000;

        if (value < k) {
            return value + " B";
        } else if (value < m) {
            return Math.round(value / k) + " kB";
        } else if (value < g) {
            return Math.round(value / m) + " MB";
        } else if (value <= t) {
            return Math.round(value / g) + " GB";
        } else if (value <= p) {
            return Math.round(value / t) + " TB";
        }
    }

    formatBiBytes(value: any) {
        const k = 1024;
        const m = k * 1024;
        const g = m * 1024;
        const t = g * 10124;
        const p = t * 1024;

        if (value < k) {
            return value + " B";
        } else if (value < m) {
            return Math.round(value / k) + " kB";
        } else if (value < g) {
            return Math.round(value / m) + " MB";
        } else if (value <= t) {
            return Math.round(value / g) + " GB";
        } else if (value <= p) {
            return Math.round(value / t) + " TB";
        }
    }

    /*
     * TRANSLATORS
     * Does conversion if needed. Applies rounding if requested.
     */


    /**
     * Translates a temperature difference in Fahrenheit into the appropriate unit for the current locale,
     * applying scale conversion (if needed), rounding, and formatting.
     * @see convertFahrenheitDiff2celsiusDiff()
     */
    translateFahrenheitDiff2locale(value:number, decimals?: number, rounding?:  Rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.temperatureType !== "f") {
            value = this.convertFahrenheitDiff2celsiusDiff(value);
        }
        return this.round(value, decimals, rounding);
    }
    /**
     * Translates a temperature difference from the current locale's unit into Fahrenheit,
     * applying scale conversion (if needed), rounding, and formatting.
     * @see convertCelsiusDiff2fahrenheitDiff()
     */
    translateLocale2fahrenheitDiff(value: number, decimals?: number, rounding?: Rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.temperatureType !== "f") {
            value = this.convertCelsiusDiff2fahrenheitDiff(value);
        }
        return this.round(value, decimals, rounding);
    }

    translateFahrenheit2locale(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.temperatureType !== "f") {
            value = this.convertFahrenheit2celsius(value);
        }
        return this.round(value, decimals, rounding);
    }
    translateLocale2fahrenheit(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.temperatureType !== "f") {
            value = this.convertCelsius2fahrenheit(value);
        }
        return this.round(value, decimals, rounding);
    }

    translateMile2locale(value, decimals, rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.lengthUnit !== measurementSystems.IMPERIAL) {
            value = this.convertMile2kilometer(value);
        }
        return this.round(value, decimals, rounding);
    }
    translateLocale2mile(value, decimals, rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.lengthUnit !== measurementSystems.IMPERIAL) {
            value = this.convertKilometer2mile(value);
        }
        return this.round(value, decimals, rounding);
    }
    translateKm2locale(value, decimals, rounding) {
        if (!this.isConvertible(value)) return value;
        if (this.lengthUnit === measurementSystems.IMPERIAL) {
            value = this.convertKilometer2mile(value);
        }
        return this.round(value, decimals, rounding);
    }

    translateGallon2locale(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.volumeUnit !== measurementSystems.IMPERIAL) {
            value = this.convertGallon2liter(value);
        }
        return this.round(value, decimals, rounding);
    }
    translateLocale2gallon(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.volumeUnit !== measurementSystems.IMPERIAL) {
            value = this.convertLiter2gallon(value);
        }
        return this.round(value, decimals, rounding);
    }

    /**
     * Converts to milliliters if needed.
     */
    translateOunces2locale(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.volumeUnit !== measurementSystems.IMPERIAL) {
            value = this.convertOunces2milliliter(value);
        }
        return this.round(value, decimals, rounding);
    }
    /**
     * Converts from milliliters if needed.
     */
    translateLocale2ounces(value, decimals?, rounding?) {
        if (!this.isConvertible(value)) return value;
        if (this.volumeUnit !== measurementSystems.IMPERIAL) {
            value = this.convertMilliliter2ounces(value);
        }
        return this.round(value, decimals, rounding);
    }

    translatePressureToLocale(value: any, decimals: number | false, rounding: Rounding, localeUnit: "bar" | "pa" = "pa") {
        if (!this.isConvertible(value)) return value;
        if (this.lengthUnit !== measurementSystems.IMPERIAL) {
            if (localeUnit === "pa") {
                value = this.convertPsiTokPa(value);
            } else if (localeUnit === "bar") {
                value = this.convertPsiToBar(value);
            }
        }
        return this.round(value, decimals, rounding);
    }
    /**
     * 
     * @param value Pressure in kPa or Bar
     * @param decimals 
     * @param rounding 
     * @param localeUnit Input unit, Bar or Pascal
     * @returns 
     */
    translateLocaleToPressure(value: any, decimals: number | false, rounding: Rounding, localeUnit: "bar" | "pa" = "pa") {
        if (!this.isConvertible(value)) return value;
        if (this.lengthUnit !== measurementSystems.IMPERIAL) {
            if (localeUnit === "pa") {
                value = this.convertkPaToPsi(value);
            } else if (localeUnit === "bar") {
                value = this.convertBarToPsi(value);
            }
        }
        return this.round(value, decimals, rounding);
    }

    

    /*
     * CONVERTERS
     */

    convertGallon2liter(val) {
        return val * usLiquidGallonInLiter;
    }
    convertLiter2gallon(val) {
        return val / usLiquidGallonInLiter;
    }

    convertMile2kilometer(val) {
        return val * mileInKilometer;
    }
    convertKilometer2mile(val) {
        return val / mileInKilometer;
    }

    convertFoot2meter(val) {
        return val * footInMeter;
    }
    convertMeter2Foot(val) {
        return val / footInMeter;
    }

    convertFahrenheit2celsius(val) {
        return (val - fahrenheitOffset) / fahrenheitSlope;
    }
    convertCelsius2fahrenheit(val) {
        return (val * fahrenheitSlope) + fahrenheitOffset;
    }

    /**
     * Converts a temperature difference from Fahrenheit to Celsius.
     * (i.e., converts the distance between two Fahrenheit values into the equivalent distance in Celsius.)
     * 
     * Example:
     *   A difference of 5°F ≈ 2.78°C
     * 
     *   100°F ≈ 37.78°C
     *   105°F ≈ 40.56°C
     *   The distance in Fahrenheit between 100°F and 105°F is 5°F,
     *   which is equivalent to ~2.78°C in Celsius.
     * 
     * (Note: 5°F as an absolute temperature would be -15°C using a regular F → C conversion.)
     */
    convertFahrenheitDiff2celsiusDiff(val) {
        return val / fahrenheitSlope;
    }

    /**
     * Converts a temperature difference from Celsius to Fahrenheit.
     * (i.e., converts the distance between two Celsius values into the equivalent distance in Fahrenheit.)
     * 
     * Example:
     *   A difference of 2.78°C ≈ 5°F
     * 
     *   37.78°C ≈ 100°F
     *   40.56°C ≈ 105°F
     *   The distance in Celsius between 37.78°C and 40.56°C is ~2.78°C,
     *   which is equivalent to 5°F in Fahrenheit.
     * 
     * (Note: 2.78°C as an absolute temperature would be 36.996°F using a regular C → F conversion.)
     */
    convertCelsiusDiff2fahrenheitDiff(val) {
        return val * fahrenheitSlope;
    }

    convertCiubicFoot2Gallon(val) {
        return val * cubicFootInUsLiquidGallon
    }

    convertCubicMeter2Liter(val: number) {
        return val * 1000;
    }

    convertOunces2milliliter(val) {
        return val * fluidOunceInMilliLiter;
    }
    convertMilliliter2ounces(val) {
        return val / fluidOunceInMilliLiter;
    }

    convertPsiTokPa(value: number): number {
        return value / PSI_PER_KPA;
    }
    convertkPaToPsi(value: number): number {
        return value / KPA_PER_PSI;
    }

    convertPsiToBar(value: number): number {
        return value / PSI_PER_BAR; ;
    }
    convertBarToPsi(value: number): number {
        return value / BAR_PER_PSI
    }

    /** Converts date/time format sring from Moment.js to Date-Fns */
    momentjs2dateFns(format) {
        // https://github.com/date-fns/date-fns/issues/2991#issuecomment-1882535400
        return format
            .replace(/LT/g, "p")
            .replace(/LTS/g, "pp")
            .replace(/LLLL|llll/g, "PPPPp")
            .replace(/LLL|lll/g, "PPp")
            .replace(/L|l/g, "P")
            .replace(/Y/g, "y")
            .replace(/g/g, "Y")
            .replace(/G/g, "R")
            .replace(/W/g, "l")
            .replace(/E/g, "i")
            .replace(/(D+|d+)o?/g, (inp: string) => {
                switch (inp) {
                    case "D": // 1 2 ... 30 31
                    case "Do": // 1st 2nd ... 30th 31st
                    case "DD": // 01 02 ... 30 31
                    return inp.replace(/D/g, "d");
                    case "DDD": // 1 2 ... 364 365
                    return "D";
                    case "DDDo": // 1st 2nd ... 364th 365th
                    return "Do";
                    case "DDDD": // 001 002 ... 364 365
                    return "DDD";
        
                    case "dd": // Su Mo ... Fr Sa
                    return "eeeeee";
                    case "d": // 0 1 ... 5 6
                    case "do": // 0th 1st ... 5th 6th
                    case "ddd": // Sun Mon ... Fri Sat
                    case "dddd": // Sunday Monday ... Friday Saturday
                    return inp.replace(/d/g, "e");// will actually return value between 1 and 7 for the numerical results
                }
                return inp;
            })
            // Can't properly convert. DateFns does not have am/pm only a.m. and p.m.
            // Moment.js a: am/pm, A: AM/PM
            // DateFns aaaa: a.m./p.m., a..aaa: AM/PM
            .replace(/a/g, "aaaa")
            .replace(/A/g, "aa")
            .replace(/X/g, "t")
            .replace(/x/g, "T")
            .replace(/ZZ|zz/g, "xx")
            .replace(/Z|z/g, "xxx");
    }

}

const locale = new Locale();

export default locale;

/**
 * Utility function for Apps to provide common way of setting up Locale.
 * @param localeInstance
 * @param extensions 
 * @param monitorErrors
 */
export function setupLocale(localeInstance: Locale, extensions?: any[], monitorErrors = false): void {
    if (extensions) {
        localeInstance.addExtension(extensions);
    }
    localeInstance.monitorErrors = monitorErrors;
}

